/**
 * StrategyPickerModal Component
 *
 * Reusable modal for selecting strategies when moving, copying, or sharing playbooks.
 * Supports three modes with different selection and filtering behaviors:
 * - move: Single-select, excludes current strategy and strategies already containing playbook
 * - copy: Single-select, includes all strategies (current pre-selected by default)
 * - share: Multi-select, excludes strategies already containing playbook
 *
 * Includes inline strategy creation capability.
 */

// 1. External dependencies
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Eye, EyeOff, Search, Plus, Check } from 'lucide-react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Checkbox,
  Alert,
  useToastNotification,
} from '@enfomentis/ui';

// 3. Services & Types
import { strategyService } from '@/services/StrategyService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { Strategy } from '@/types/schema/Strategy';

// =============================================================================
// Types
// =============================================================================

/** Selection mode for the strategy picker */
export type StrategyPickerMode = 'move' | 'copy' | 'share';

/**
 * Props for StrategyPickerModal
 */
export interface StrategyPickerModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Callback after confirmation - receives array of selected strategy IDs */
  onConfirm: (strategyIds: string[]) => void;
  /** Selection mode determining behavior */
  mode: StrategyPickerMode;
  /** ID of the playbook being moved/copied/shared */
  playbookId: string;
  /** ID of the current strategy the playbook belongs to */
  currentStrategyId: string;
  /** Name of the playbook for display in header */
  playbookName: string;
}

// =============================================================================
// Helpers
// =============================================================================

/** Sort alphabetically by name (case-insensitive). Returns a new sorted array. */
const sortByName = <T extends { name: string }>(items: T[]): T[] =>
  [...items].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );

/** Get modal title based on mode */
const getModalTitle = (mode: StrategyPickerMode): string => {
  switch (mode) {
    case 'move':
      return 'Move to Strategy';
    case 'copy':
      return 'Copy to Strategy';
    case 'share':
      return 'Share with Strategies';
  }
};

/** Get confirm button text based on mode, selection count, and create state */
const getConfirmButtonText = (
  mode: StrategyPickerMode,
  count: number,
  isCreating: boolean
): string => {
  if (isCreating) {
    switch (mode) {
      case 'move':
        return 'Create & Move';
      case 'copy':
        return 'Create & Copy';
      case 'share':
        return 'Create & Share';
    }
  }

  switch (mode) {
    case 'move':
      return 'Move';
    case 'copy':
      return 'Copy';
    case 'share':
      return count === 1 ? 'Share (1)' : `Share (${count})`;
  }
};

/** Get empty state message based on mode */
const getEmptyStateMessage = (mode: StrategyPickerMode): string => {
  switch (mode) {
    case 'move':
      return 'This playbook is already in all available strategies.';
    case 'share':
      return 'This playbook is already shared with all strategies.';
    case 'copy':
      return 'No strategies available.';
  }
};

// =============================================================================
// Component
// =============================================================================

/**
 * StrategyPickerModal Component
 */
export const StrategyPickerModal: React.FC<StrategyPickerModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  mode,
  playbookId,
  currentStrategyId,
  playbookName,
}) => {
  // Toast notifications
  const { showToast } = useToastNotification();

  // ---------------------------------------------------------------------------
  // Data State
  // ---------------------------------------------------------------------------

  const [allStrategies, setAllStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // ---------------------------------------------------------------------------
  // UI State
  // ---------------------------------------------------------------------------

  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Inline creation state
  const [isCreating, setIsCreating] = useState(false);
  const [newStrategyName, setNewStrategyName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [isSubmittingCreate, setIsSubmittingCreate] = useState(false);

  // ---------------------------------------------------------------------------
  // Data Loading
  // ---------------------------------------------------------------------------

  const loadStrategies = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const strategies = await strategyService.getAll();
      setAllStrategies(strategies);
    } catch (error) {
      console.error('Failed to load strategies:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setShowArchived(false);
      setIsCreating(false);
      setNewStrategyName('');
      setCreateError(null);

      // Mode-specific initial selection
      if (mode === 'copy') {
        // Pre-select current strategy for copy mode
        setSelectedIds(new Set([currentStrategyId]));
      } else {
        // Empty selection for move/share modes
        setSelectedIds(new Set());
      }

      loadStrategies();
    }
  }, [isOpen, mode, currentStrategyId, loadStrategies]);

  // ---------------------------------------------------------------------------
  // Filtering Logic
  // ---------------------------------------------------------------------------

  // Get IDs of strategies that already contain this playbook
  const strategiesContainingPlaybook = useMemo(() => {
    return new Set(
      allStrategies
        .filter((s) => s.playbookIds?.includes(playbookId))
        .map((s) => s.id)
    );
  }, [allStrategies, playbookId]);

  // Filter strategies based on mode-specific exclusion rules
  const selectableStrategies = useMemo(() => {
    return allStrategies.filter((s) => {
      // Always exclude tombstoned strategies
      if (s.deletedAt) return false;

      switch (mode) {
        case 'move':
          // Exclude current strategy AND strategies already containing playbook
          return s.id !== currentStrategyId && !strategiesContainingPlaybook.has(s.id);
        case 'copy':
          // No exclusions - all strategies are valid targets
          return true;
        case 'share':
          // Exclude strategies already containing playbook (including current)
          return !strategiesContainingPlaybook.has(s.id);
      }
    });
  }, [allStrategies, mode, currentStrategyId, strategiesContainingPlaybook]);

  // Apply search filter
  const filteredStrategies = useMemo(() => {
    if (!searchQuery.trim()) {
      return selectableStrategies;
    }
    const query = searchQuery.toLowerCase().trim();
    return selectableStrategies.filter((s) =>
      s.name.toLowerCase().includes(query)
    );
  }, [selectableStrategies, searchQuery]);

  // Group into active and archived
  const activeStrategies = useMemo(
    () => sortByName(filteredStrategies.filter((s) => s.status === 'active')),
    [filteredStrategies]
  );

  const archivedStrategies = useMemo(
    () => sortByName(filteredStrategies.filter((s) => s.status === 'archived')),
    [filteredStrategies]
  );

  // Check if there are any selectable strategies at all
  const hasSelectableStrategies = selectableStrategies.length > 0;

  // ---------------------------------------------------------------------------
  // Selection Handlers
  // ---------------------------------------------------------------------------

  const handleToggleSelection = (strategyId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (mode === 'share') {
        // Multi-select for share mode
        if (next.has(strategyId)) {
          next.delete(strategyId);
        } else {
          next.add(strategyId);
        }
      } else {
        // Single-select for move/copy modes
        next.clear();
        next.add(strategyId);
      }

      return next;
    });
  };

  // ---------------------------------------------------------------------------
  // Inline Creation
  // ---------------------------------------------------------------------------

  const handleStartCreate = () => {
    setIsCreating(true);
    setNewStrategyName('');
    setCreateError(null);
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewStrategyName('');
    setCreateError(null);
  };

  /**
   * Core strategy creation logic - shared between inline create and create-and-confirm flows.
   * Returns the new strategy ID on success, null on failure.
   */
  const createStrategyCore = async (name: string): Promise<string | null> => {
    try {
      const newStrategy = await strategyService.add({
        name,
        playbookIds: [],
      });

      // Sync notifications - local first for same-tab, then cross-tab
      syncCoordinatorService.notifyLocalChange('strategies', newStrategy.id);
      syncCoordinatorService.notifyChange('strategies', newStrategy.id);

      // Add to local list
      setAllStrategies((prev) => [...prev, newStrategy]);

      return newStrategy.id;
    } catch (error) {
      console.error('Failed to create strategy:', error);
      return null;
    }
  };

  /**
   * Handle inline strategy creation (original flow with separate Create button).
   * Creates strategy, auto-selects it, shows toast, resets UI.
   */
  const handleCreateStrategy = async () => {
    const trimmedName = newStrategyName.trim();
    if (!trimmedName) {
      setCreateError('Name is required');
      return;
    }

    setIsSubmittingCreate(true);
    setCreateError(null);

    const newId = await createStrategyCore(trimmedName);

    if (newId) {
      // Auto-select the new strategy
      if (mode === 'share') {
        setSelectedIds((prev) => new Set([...prev, newId]));
      } else {
        setSelectedIds(new Set([newId]));
      }

      showToast({
        title: 'Strategy Created',
        description: `"${trimmedName}" has been created`,
        color: 'success',
      });

      // Reset creation UI
      setIsCreating(false);
      setNewStrategyName('');
    } else {
      setCreateError('Failed to create strategy. Please try again.');
    }

    setIsSubmittingCreate(false);
  };

  /**
   * Handle create-and-confirm flow (footer button triggers both creation and action).
   * Creates strategy and immediately calls onConfirm with the new ID.
   */
  const handleCreateAndConfirm = async () => {
    const trimmedName = newStrategyName.trim();
    if (!trimmedName) {
      setCreateError('Name is required');
      return;
    }

    setIsSubmittingCreate(true);
    setCreateError(null);

    const newId = await createStrategyCore(trimmedName);

    if (newId) {
      // Call onConfirm directly with the new strategy ID
      onConfirm([newId]);
      onClose();
    } else {
      setCreateError('Failed to create strategy. Please try again.');
      setIsSubmittingCreate(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Confirm Handler
  // ---------------------------------------------------------------------------

  const handleConfirm = () => {
    // If in create mode, route to create-and-confirm
    if (isCreating) {
      handleCreateAndConfirm();
      return;
    }

    // Normal flow: confirm with selected strategies
    if (selectedIds.size === 0) return;
    onConfirm(Array.from(selectedIds));
    onClose();
  };

  // ---------------------------------------------------------------------------
  // Render Helpers
  // ---------------------------------------------------------------------------

  const renderStrategyItem = (strategy: Strategy) => {
    const isSelected = selectedIds.has(strategy.id);
    const isSingleSelect = mode === 'move' || mode === 'copy';

    return (
      <div
        key={strategy.id}
        className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-default-100 cursor-pointer"
        onClick={() => handleToggleSelection(strategy.id)}
      >
        {/* Selection indicator */}
        <div onClick={(e) => e.stopPropagation()}>
          {isSingleSelect ? (
            // Radio-like indicator for single-select
            <div
              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                isSelected
                  ? 'border-primary bg-primary'
                  : 'border-default-300'
              }`}
            >
              {isSelected && <Check size={10} className="text-white" />}
            </div>
          ) : (
            // Checkbox for multi-select
            <Checkbox
              isSelected={isSelected}
              onValueChange={() => handleToggleSelection(strategy.id)}
              aria-label={`Select ${strategy.name}`}
            />
          )}
        </div>
        <span
          className={`flex-1 truncate ${
            strategy.status === 'archived' ? 'text-text-tertiary' : 'text-text-primary'
          }`}
        >
          {strategy.name}
        </span>
        {strategy.status === 'archived' && (
          <span className="text-xs text-text-tertiary bg-default-100 px-1.5 py-0.5 rounded shrink-0">
            Archived
          </span>
        )}
      </div>
    );
  };

  /**
   * Render inline create section - reusable for both empty state and list bottom.
   * @param isEmptyState - true for centered empty state styling, false for full-width list styling
   * @param hideButtons - when true and creating, show only input (footer handles actions)
   */
  const renderInlineCreate = (isEmptyState: boolean, hideButtons = false) => {
    if (!isCreating) {
      return (
        <Button
          variant="flat"
          color={isEmptyState ? 'primary' : 'default'}
          startContent={<Plus size={16} />}
          onPress={handleStartCreate}
          className={isEmptyState ? '' : 'w-full'}
        >
          Create New Strategy
        </Button>
      );
    }

    // When hideButtons is true, show only the input - footer buttons handle actions
    if (hideButtons) {
      return (
        <div className="space-y-3">
          <p className="text-sm text-text-secondary">
            Enter a name for the new strategy:
          </p>
          <Input
            placeholder="Strategy name"
            value={newStrategyName}
            onValueChange={setNewStrategyName}
            isInvalid={!!createError}
            errorMessage={createError}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                handleCancelCreate();
              }
              // Note: Enter key handled by footer button
            }}
            className="[&_input]:text-base"
          />
        </div>
      );
    }

    // Original behavior with inline Create/Cancel buttons
    return (
      <div className="space-y-2">
        <Input
          placeholder="Enter strategy name"
          value={newStrategyName}
          onValueChange={setNewStrategyName}
          isInvalid={!!createError}
          errorMessage={createError}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newStrategyName.trim()) {
              handleCreateStrategy();
            } else if (e.key === 'Escape') {
              handleCancelCreate();
            }
          }}
          className="[&_input]:text-base"
        />
        <div className="flex gap-2 justify-end">
          <Button
            size="sm"
            variant="flat"
            onPress={handleCancelCreate}
            isDisabled={isSubmittingCreate}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            color="primary"
            onPress={handleCreateStrategy}
            isLoading={isSubmittingCreate}
            isDisabled={!newStrategyName.trim()}
          >
            Create
          </Button>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      placement="top-center"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader>
          <div className="flex flex-col">
            <span>{getModalTitle(mode)}</span>
            <span className="text-sm font-normal text-text-secondary">
              {playbookName}
            </span>
          </div>
        </ModalHeader>

        <ModalBody className="gap-4">
          {/* Create Mode - Show only name input, footer handles actions */}
          {isCreating && (
            <div className="py-4">
              {renderInlineCreate(true, true)}
            </div>
          )}

          {/* Normal Mode - Search, list, inline create */}
          {!isCreating && (
            <>
              {/* Search Input */}
              {hasSelectableStrategies && (
                <Input
                  placeholder="Search strategies..."
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                  startContent={<Search size={16} className="text-text-tertiary" />}
                  isClearable
                  onClear={() => setSearchQuery('')}
                  className="[&_input]:text-base"
                />
              )}

              {/* Loading State */}
              {loading && (
                <div className="text-center py-8">
                  <p className="text-text-secondary">Loading strategies...</p>
                </div>
              )}

              {/* Error State */}
              {loadError && !loading && (
                <div className="space-y-2">
                  <Alert color="danger" variant="flat">
                    Failed to load strategies. Please try again.
                  </Alert>
                  <Button size="sm" variant="flat" onPress={loadStrategies}>
                    Retry
                  </Button>
                </div>
              )}

              {/* Empty State - No selectable strategies */}
              {!loading && !loadError && !hasSelectableStrategies && (
                <div className="text-center py-8 space-y-4">
                  <p className="text-text-secondary">
                    {getEmptyStateMessage(mode)}
                  </p>
                  {renderInlineCreate(true)}
                </div>
              )}

              {/* Strategy List */}
              {!loading && !loadError && hasSelectableStrategies && (
                <div className="space-y-4">
                  {/* Active Section */}
                  {activeStrategies.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-text-secondary">
                          Active ({activeStrategies.length})
                        </h4>
                      </div>
                      <div className="space-y-1">
                        {activeStrategies.map(renderStrategyItem)}
                      </div>
                    </div>
                  )}

                  {/* Show Archived Toggle */}
                  <div className="flex items-center justify-between py-2 border-t border-default-200">
                    <Button
                      size="sm"
                      variant="light"
                      startContent={showArchived ? <Eye size={16} /> : <EyeOff size={16} />}
                      onPress={() => setShowArchived((prev) => !prev)}
                    >
                      {showArchived ? 'Hide Archived' : 'Show Archived'}
                    </Button>
                    {showArchived && archivedStrategies.length > 0 && (
                      <span className="text-sm text-text-tertiary">
                        {archivedStrategies.length} archived
                      </span>
                    )}
                  </div>

                  {/* Archived Section */}
                  {showArchived && archivedStrategies.length > 0 && (
                    <div>
                      <div className="space-y-1">
                        {archivedStrategies.map(renderStrategyItem)}
                      </div>
                    </div>
                  )}

                  {/* No results from search */}
                  {filteredStrategies.length === 0 && searchQuery.trim() && (
                    <div className="text-center py-4">
                      <p className="text-text-secondary text-sm">
                        No strategies match "{searchQuery}"
                      </p>
                    </div>
                  )}

                  {/* Hint when results are only in archived */}
                  {activeStrategies.length === 0 && archivedStrategies.length > 0 && !showArchived && (
                    <div className="text-center py-4">
                      <p className="text-text-secondary text-sm">
                        {searchQuery.trim()
                          ? 'Results are in archived strategies'
                          : 'All selectable strategies are archived'}
                      </p>
                    </div>
                  )}

                  {/* Inline Create Section */}
                  <div className="border-t border-default-200 pt-4">
                    {renderInlineCreate(false)}
                  </div>
                </div>
              )}
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            variant="flat"
            onPress={isCreating ? handleCancelCreate : onClose}
          >
            {isCreating ? 'Back' : 'Cancel'}
          </Button>
          <Button
            color="primary"
            onPress={handleConfirm}
            isLoading={isCreating && isSubmittingCreate}
            isDisabled={
              isCreating
                ? !newStrategyName.trim()
                : selectedIds.size === 0
            }
          >
            {getConfirmButtonText(mode, selectedIds.size, isCreating)}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default StrategyPickerModal;
